import { useEffect, useMemo, useRef, useState } from 'react';
import { describeStaffPerformanceError } from '../services/staffPerformanceService';
import { subscribeToSuccessUpdates, subscribeToSuccessUpdatesMeta, UNASSIGNED_DOC_ID } from '../services/successUpdatesService';
import { StaffPerformanceScope, StaffSuccessUpdates, SuccessUpdatesMeta } from '../types';

interface UseSuccessUpdatesResult {
  /** Per-coach rollups keyed by staff id (the document id). Never contains the unassigned bucket. */
  byStaffId: Map<string, StaffSuccessUpdates>;
  /** Clients with no usable lead coach. Only admins can read it. */
  unassigned: StaffSuccessUpdates | null;
  meta: SuccessUpdatesMeta | null;
  /**
   * True once the docs listener has delivered a snapshot for the current scope. False while loading,
   * after a docs-listener error, and for a null or `none` scope. Without it an empty `byStaffId`
   * cannot be told apart from "this coach leads no active clients".
   */
  docsLoaded: boolean;
  error: string | null;
}

/**
 * Realtime overdue-update rollups for the signed-in user's scope. A null scope subscribes to nothing.
 * Consumers gate on `successUpdatesReady(meta, docsLoaded)` before showing per-coach data. While `docsLoaded` is
 * false (loading, after a docs error, and on the first render after the scope changes) `byStaffId` is empty and
 * `unassigned` is null, so the previous scope's documents are never surfaced under a new scope.
 */
export function useSuccessUpdates(scope: StaffPerformanceScope | null): UseSuccessUpdatesResult {
  const [docs, setDocs] = useState<StaffSuccessUpdates[]>([]);
  // The scope key the current `docs` were loaded for; null until a docs snapshot arrives, and after a docs error.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [meta, setMeta] = useState<SuccessUpdatesMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const scopeKey = scope ? JSON.stringify(scope) : null;
  const docsLoaded = scopeKey !== null && loadedKey === scopeKey;

  useEffect(() => {
    // Never show the previous scope's rollups under the new scope.
    setDocs((previous) => (previous.length === 0 ? previous : []));
    setLoadedKey(null);

    const current = scopeRef.current;
    if (!current) return undefined;

    setError(null);
    const reportError = (subscriptionError: Error) => setError(describeStaffPerformanceError(subscriptionError));
    const onDocsError = (subscriptionError: Error) => {
      setLoadedKey(null);
      reportError(subscriptionError);
    };
    const unsubscribeDocs = subscribeToSuccessUpdates(
      current,
      (next) => {
        setDocs(next);
        setLoadedKey(scopeKey);
      },
      onDocsError
    );
    const unsubscribeMeta = subscribeToSuccessUpdatesMeta(setMeta, reportError);
    return () => {
      unsubscribeDocs();
      unsubscribeMeta();
    };
  }, [scopeKey]);

  return useMemo(() => {
    const byStaffId = new Map<string, StaffSuccessUpdates>();
    let unassigned: StaffSuccessUpdates | null = null;
    // `docs` may still belong to the previous scope until the effect has reset it.
    (docsLoaded ? docs : []).forEach((entry) => {
      if (entry.id === UNASSIGNED_DOC_ID) unassigned = entry;
      else byStaffId.set(entry.id, entry);
    });
    return { byStaffId, unassigned, meta, docsLoaded, error };
  }, [docs, meta, docsLoaded, error]);
}
